import { produce } from "immer";
import * as uuid from "uuid";

import type { Write } from "../store/zustand.helpers";

import { zustandStore } from "./zustand.helpers";

// Designed based on STORM snackbar
// https://storm.advertising.amazon.dev/components/snackbar/?tab=code
export enum NotificationType {
    INFO = "info",
    WARNING = "warning",
    ERROR = "error",
    SUCCESS = "success",
    LOADING = "loading",
}

type NotificationMessage = string | JSX.Element;
export type Notification = {
    message: NotificationMessage;
    type?: NotificationType;
    identifier: string;
    updateTimestamp: Date;
};

export type NotificationState = {
    notifications: Notification[];
};

export interface NotificationActions {
    upsertNotification: (
        message: NotificationMessage,
        type?: NotificationType,
        executionId?: string,
    ) => void;
    dismissNotification: (identifier: string) => void;
    dismissExpiredNotifications: (expirationInMs: number) => void;
    resetNotifications: () => void;
    findNotificationByIdentifer: (
        identifier: string,
    ) => Notification | undefined;
}

export const initialState: NotificationState = {
    notifications: [],
};

export const { store: notificationStore } = zustandStore<
    NotificationState,
    NotificationActions
>("Notification", initialState, (set, get) => ({
    upsertNotification: (
        message: NotificationMessage,
        type?: NotificationType,
        executionId?: string,
    ) => {
        const identifier = executionId ?? uuid.v4();
        set(
            produce((draft: NotificationState) => {
                const currentTimestamp = new Date();
                const notificationIndex = draft.notifications.findIndex(
                    (notif) => notif.identifier === identifier,
                );
                // If there is an notification with that identifier
                if (notificationIndex >= 0) {
                    // Find it and pull it out
                    const existingNotification = draft.notifications.splice(
                        notificationIndex,
                        1,
                    )[0];
                    // Update it
                    existingNotification.message = message;
                    existingNotification.type = type;
                    existingNotification.updateTimestamp = currentTimestamp;
                    // Add it back in
                    draft.notifications.push(existingNotification);
                } else {
                    draft.notifications.push({
                        message,
                        type,
                        identifier,
                        updateTimestamp: currentTimestamp,
                    });
                }
            }),
        );
    },
    dismissNotification: (identifier: string) => {
        set(
            produce((draft: NotificationState) => {
                const notificationIndex = draft.notifications.findIndex(
                    (notif) => notif.identifier === identifier,
                );
                if (notificationIndex >= 0) {
                    draft.notifications.splice(notificationIndex, 1);
                }
            }),
        );
    },
    dismissExpiredNotifications: (expirationInMs: number) => {
        set(
            produce((draft: NotificationState) => {
                const currentTimestamp = new Date();
                const expirationInSeconds: number = Math.floor(
                    expirationInMs / 1000,
                );

                draft.notifications = draft.notifications.filter((notif) => {
                    const secondsSinceUpsert = Math.ceil(
                        (currentTimestamp.getTime() -
                            notif.updateTimestamp.getTime()) /
                            1000,
                    );
                    return secondsSinceUpsert < expirationInSeconds;
                });
            }),
        );
    },
    resetNotifications: () =>
        set(
            produce((draft: NotificationState) => {
                draft.notifications = initialState.notifications;
            }),
        ),

    findNotificationByIdentifer: (identifier) => {
        const store = get() as Write<NotificationState, NotificationActions>;
        return store.notifications.find(
            (notif) => notif.identifier === identifier,
        );
    },
}));
